import _ from 'lodash';
import { createReducer } from 'redux-act';
import Immutable from 'seamless-immutable';

import RefData from 'ref-data';
import { PROVIDER_CONFIGS } from '../../common/components/Privacy/Providers/Config';
import DisplayContainerActions from './display-container-actions';

const initialState = Immutable({
  byId:               {},
  isFetching:         false,
  hasFetched:         false,
  thirdPartyProvider: {
    isFetching: false,
    hasFetched: false,
    formatType: '',
    code:       _.merge({}, PROVIDER_CONFIGS), //this initial config will be extended by loading more code from an api
  },
  vanityPath: {
    isModalVisible: false,
    containerId:    null,
    value:          '',
  },
});

const reducer = createReducer((on) => {
  on(DisplayContainerActions.fetchWithEmbedCodes, (state) => {
    return state.set('isFetching', true);
  });

  on(DisplayContainerActions.fetchWithEmbedCodesFailure, (state) => {
    return state.set('isFetching', false).set('hasFetched', true);
  });

  on(
    [DisplayContainerActions.fetchDisplayContainersSuccess, DisplayContainerActions.fetchWithEmbedCodesSuccess],
    (state, { displayContainers }) => {
      const byId = _.reduce(
        displayContainers,
        (acc, displayContainer) => {
          acc[displayContainer.id] = displayContainer;
          return acc;
        },
        {}
      );
      return state.set('byId', byId).set('isFetching', false).set('hasFetched', true);
    }
  );

  on(DisplayContainerActions.add, (state, { displayContainers }) => {
    let displayContainersById = _.reduce(
      displayContainers,
      (acc, displayContainer) => {
        acc[displayContainer.id] = displayContainer;
        return acc;
      },
      {}
    );
    displayContainersById = state.byId.merge(displayContainersById);
    return state.set('byId', displayContainersById);
  });

  on(DisplayContainerActions.updateContainer, (state, { containerId, update }) => {
    return state.setIn(['byId', containerId, update.id], update.value);
  });

  on(DisplayContainerActions.resetDisplayContainer, (state, { container }) =>
    state.setIn(['byId', container.id], container)
  );

  on(DisplayContainerActions.saveDisplayContainer, (state, { displayContainer }) =>
    state.setIn(['byId', displayContainer.id], displayContainer)
  );

  on(DisplayContainerActions.saveDisplayContainerSuccess, (state, { container }) => {
    const containers = _.isArray(container) ? container : [container];
    let containersById = _.reduce(
      containers,
      (acc, container) => {
        acc[container.id] = container;
        return acc;
      },
      {}
    );
    containersById = state.byId.merge(containersById);
    return state.set('byId', containersById);
  });

  on(
    [DisplayContainerActions.deleteContainerSuccess, DisplayContainerActions.removeDisplayContainer],
    (state, { id }) => state.set('byId', state.byId.without(id))
  );

  on(DisplayContainerActions.expireAll, (state) => {
    const byId = _.mapValues(state.byId, (displayContainer) =>
      displayContainer.set('status', RefData.Enum.STATUS.DISPLAYCONTAINER.EXPIRED)
    );
    return state.set('byId', byId);
  });

  // update on success
  on(
    [DisplayContainerActions.expireContainerSuccess, DisplayContainerActions.publishContainerSuccess],
    (state, { container }) => {
      return state.setIn(['byId', container.id], container);
    }
  );

  // eagerly set the default
  on(DisplayContainerActions.setDefaultSuccess, (state, { container }) => {
    const byId = _.mapValues(state.byId, (c) => c.set('isDefault', c.id === container.id));
    return state.set('byId', byId);
  });

  // eagerly set the root
  on(DisplayContainerActions.setRootSuccess, (state, { container }) => {
    const byId = _.mapValues(state.byId, (c) => {
      if (c.id === container.id) {
        // Set new root and update smartUrlId
        return c.set('isRoot', true).set('smartUrlId', container.smartUrlId);
      } else if (c.isRoot) {
        // If previously root, set smartUrlId as smartUrlWithShortCode
        return c.set('isRoot', false).set('smartUrlId', c.smartUrlWithShortCode);
      } else {
        return c.set('isRoot', false);
      }
    });
    return state.set('byId', byId);
  });

  //for 3rd party provider config, we keep the javascript/css/html code in corelib,
  //so that is the 'single source of truth', making it easy to update it in one place and get it available in coreapp console, macro display and nextgen display
  on(DisplayContainerActions.fetchThirdPartyProviderCode, (state) =>
    state.setIn(['thirdPartyProvider', 'isFetching'], true)
  );

  on(DisplayContainerActions.fetchThirdPartyProviderCodeFailure, (state) =>
    state.setIn(['thirdPartyProvider', 'isFetching'], false).setIn(['thirdPartyProvider', 'hasFetched'], true)
  );

  on([DisplayContainerActions.fetchThirdPartyProviderCodeSuccess], (state, { thirdPartyProviderCode, formatType }) =>
    state
      .setIn(['thirdPartyProvider', 'code'], _.merge({}, PROVIDER_CONFIGS, thirdPartyProviderCode))
      .setIn(['thirdPartyProvider', 'formatType'], formatType)
      .setIn(['thirdPartyProvider', 'isFetching'], false)
      .setIn(['thirdPartyProvider', 'hasFetched'], true)
  );

  on(DisplayContainerActions.toggleVanityPathModal, (state, { containerId, smartUrlPath }) => {
    const _smartUrlPath = smartUrlPath || '';
    const firstSlashIndex = _smartUrlPath.indexOf('/');
    const vanityPath = firstSlashIndex !== -1 ? _smartUrlPath.substring(firstSlashIndex + 1) : _smartUrlPath;
    return state.set('vanityPath', {
      value:          vanityPath,
      isModalVisible: !state.vanityPath.isModalVisible,
      containerId,
    });
  });

  on(DisplayContainerActions.setVanityPath, (state, { vanityPath }) =>
    state.setIn(['vanityPath', 'value'], vanityPath)
  );

  on(DisplayContainerActions.saveVanityPathSuccess, (state, { container }) => {
    const byId = _.mapValues(state.byId, (c) =>
      c.id === container.id ? c.set('smartUrlPath', container.smartUrlPath).set('smartUrlId', container.smartUrlId) : c
    );

    return state.set('byId', byId).set('vanityPath', initialState.vanityPath);
  });
}, initialState);

export default reducer;
